import { Alert } from "@mui/material";
import { useGetLocale } from "@refinedev/core";
import React, { useMemo } from "react";
import { useDataGrid } from "@refinedev/mui";
import { DataGrid } from "@mui/x-data-grid";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { getOrderListColumns, TabColumnInfo } from "../utils/orderListColumns";
import { getOrderFilterStore } from "../utils/getOrderFilterStore";
import { useStore } from "zustand";
import { getQueryFromFilter } from "../../../components/dataGridFilter/getQueryFromFilter";
import { FilterProvider } from "../../../components/dataGridFilter/filter-context/FilterProvider";
import { SearchIndicators } from "../../../components/dataGridFilter/SearchIndicators";
import { OrderListFilterRow } from "./OrderListFilterRow";
import { trTR, enUS } from "@mui/x-data-grid/locales";
import { SyncrosaleLoader } from "../../../components/layout/components/SyncrosaleLoader";
import { useNavigate } from "react-router-dom";

type OrdersTabProps = {
  tabInfo: TabColumnInfo;
};

export const OrdersTab: React.FC<OrdersTabProps> = ({ tabInfo }) => {
  const locale = useGetLocale();
  const currentLocale = locale();
  const navigate = useNavigate();
  const { translate } = useTranslation("pages.orders");
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const baseColumns = useMemo(
    () => getOrderListColumns(translate, navigate, currentLocale),
    [translate, currentLocale],
  );

  const dynamicColumns = useMemo(() => {
    if (tabInfo.columns) {
      return baseColumns.filter((column) =>
        tabInfo.columns!.includes(column.field),
      );
    } else {
      return baseColumns;
    }
  }, [tabInfo, baseColumns]);

  const filterStore = getOrderFilterStore(tabInfo.filterField);

  const filterStatus = useStore(filterStore);
  const query = useMemo(
    () => getQueryFromFilter(filterStatus.filters),
    [filterStatus.filters, getQueryFromFilter],
  );

  const {
    dataGridProps,
    sorters,
    tableQuery: { isInitialLoading },
  } = useDataGrid({
    resource: `store/${selectedCountry?.id}/order/optimized`,
    queryOptions: { enabled: !!selectedCountry },
    sorters: { initial: [{ field: "created", order: "desc" }] },
    meta: {
      queries: query,
    },
  });

  if (isInitialLoading) return <SyncrosaleLoader />;

  return (
    <FilterProvider filterStore={filterStore}>
      {tabInfo.filterField == "delayedPurchase" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate("list.delayed.listAlert")}
        </Alert>
      )}
      <OrderListFilterRow sorters={sorters} />
      <SearchIndicators />
      <DataGrid
        {...dataGridProps}
        columns={dynamicColumns}
        rowHeight={96}
        sx={{
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "transparent !important",
          },
          "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "transparent !important",
          },
        }}
        localeText={
          (currentLocale === "tr" ? trTR : enUS).components.MuiDataGrid
            .defaultProps.localeText
        }
        disableColumnFilter
        keepNonExistentRowsSelected
        hideFooterSelectedRowCount
      />
    </FilterProvider>
  );
};
